"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useMemo, useState } from "react";
import CompareTable from "./CompareTable";
import { ParamsItem } from "@/components/ProductFilter/type";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import DisplayImage from "@/components/ProductFilter/DisplayImage";
import { useRouter } from "next/navigation";
import { useScopedI18n } from "@/messages/client";

interface Props {
  params: {
    id: number;
    name: string;
    type: string;
    data:
      | {
          label: string;
          value: string | number;
        }[]
      | null;
  }[];
  products: {
    slug: string;
    material_number: string;
    params_data: ParamsItem[];
    images: {
      directus_files_id: string;
    }[];
    description: string;
    publish_date: string;
    is_new_product?: boolean;
    datasheet1_file?: string;
    datasheet2_file?: string;
    datasheet3_file?: string;
    package_image: string;
  }[];
}

export default function ProductsCompareContent(props: Props) {
  const t = useScopedI18n("选型器");

  const [current, setCurrent] = useState<"all" | "diff">("all");

  const columnHelper = createColumnHelper<Record<string, any>>();

  const router = useRouter();

  console.log(props.products, "props.products");

  const data = useMemo(() => {
    const origin = props.products?.map((item) => {
      return {
        images: item.package_image ? [item.package_image] : [],
        material_number: item.material_number,
        slug: item.slug,
        is_new_product: item.is_new_product,
        datasheet1_file: item.datasheet1_file,
        datasheet2_file: item.datasheet2_file,
        datasheet3_file: item.datasheet3_file,
        description: item.description,
        ...item.params_data?.reduce<Record<string, any>>((acc, cur) => {
          acc[cur.id] = cur.data.display;
          return acc;
        }, {}),
      };
    });

    return origin;
  }, [props.products]);

  const columns = useMemo(() => {
    const originalColumns = [
      columnHelper.accessor("material_number", {
        header: "Part Number",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("images", {
        header: "Images",
        cell: (info) => {
          const material_number = info.row.original.material_number;

          const images = info.getValue();

          return (
            <DisplayImage images={images} material_number={material_number} />
          );
        },
      }),
      ...props.params.map((param) => {
        return columnHelper.accessor(param.id?.toString(), {
          header: param.name,

          cell: (info) => {
            const data = info.getValue() as ParamsItem;

            if (data) {
              return data;
            }

            return "-";
          },
        });
      }),
    ];

    const diffColumns = originalColumns.reduce<
      ColumnDef<Record<string, any>, any>[]
    >((acc, cur) => {
      const curData = cur as {
        accessorKey: string;
      };

      // 提取值完全相同的key
      const isSame = (data as Record<string, any>[])
        .map((item) => {
          return item[curData.accessorKey];
        })
        .every((v, _, arr) => {
          return v === arr[0];
        });

      if (!isSame) {
        acc.push(cur);
      }

      return acc;
    }, []);

    if (current === "all") {
      return originalColumns;
    }

    if (current === "diff") {
      return diffColumns;
    }

    return originalColumns;
  }, [props.params, columnHelper, current, data]);

  const diffColumns = useMemo(() => {
    const originalColumns = [
      columnHelper.accessor("material_number", {
        header: "Part Number",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("images", {
        header: "Images",
        cell: (info) => {
          const material_number = info.row.original.material_number;

          const images = info.getValue();
          return (
            <DisplayImage images={images} material_number={material_number} />
          );
        },
      }),
      ...props.params.map((param) => {
        return columnHelper.accessor(param.id?.toString(), {
          header: param.name,

          cell: (info) => {
            const data = info.getValue() as ParamsItem;

            if (data) {
              return data;
            }

            return "-";
          },
        });
      }),
    ];

    const diffColumns = originalColumns.reduce<
      ColumnDef<Record<string, any>, any>[]
    >((acc, cur) => {
      const curData = cur as {
        accessorKey: string;
      };

      // 提取值完全相同的key
      const isSame = (data as Record<string, any>[])
        .map((item) => {
          return item[curData.accessorKey];
        })
        .every((v, _, arr) => {
          return v === arr[0];
        });

      if (!isSame) {
        acc.push(cur);
      }

      return acc;
    }, []);

    return diffColumns;
  }, [columnHelper, props.params, data]);

  return (
    <div className="flex-1 pb-10">
      <div className="relative h-[200px] bg-[#F3F4F7] text-[#0B0B0B] md:h-[280px]">
        <div className="relative z-10 h-full w-full">
          <div className="content_container relative mx-auto flex h-full items-center">
            <Button
              className="absolute left-5 top-5 h-8 rounded-md border border-[rgba(0,0,0,0.15)] bg-white py-1 text-sm text-primary md:top-10 md:h-9 md:text-base xl:left-0"
              variant="outline"
              onClick={() => {
                router.back();
              }}>
              {t("返回")}
            </Button>

            <h1 className="pl-2 text-2xl md:text-3xl lg:text-4xl xl:pl-0">
              {t("产品对比")}
            </h1>

            <div className="absolute -bottom-5 left-5 right-5 flex xl:left-0">
              <Button
                className={cn(
                  "flex-1 truncate rounded-l-md px-6 text-sm md:flex-none md:text-base",
                  {
                    "border border-[#D9D9D9] bg-white text-[#3E3E3E] hover:border-primary":
                      current === "diff",
                  }
                )}
                onClick={() => {
                  setCurrent("all");
                }}>
                {t("所有参数")}
              </Button>
              <Button
                className={cn(
                  "flex-1 truncate rounded-r-md px-6 text-sm md:flex-none md:text-base",
                  {
                    "border border-[#D9D9D9] bg-white text-[#3E3E3E] hover:border-primary":
                      current === "all",
                  }
                )}
                onClick={() => {
                  setCurrent("diff");
                }}>
                {t("只看差异")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <CompareTable data={data} columns={columns} diffColumns={diffColumns} />
    </div>
  );
}
